import React, { useEffect, useRef, useState } from 'react';
import Styled from './styled';
import AutoScroller from './AutoScroller';
import {
    ChevronDown,
    LoadingIndicator,
    NewMessageIcon,
} from '../index';

export interface BodyProps {
    /** Optional Title to the Body, displays above chat message history */
    title?: string;
    /** Optional Subtitle to the Body, displays above chat message history */
    subtitle?: string;
    /** Optional text to display when user has scrolled above the latest messages in chat thread */
    latestMessageText?: string;
    /** Optional text to display when previous messages are available to be loaded */
    loadMessagesText?: string;
    /** Message count, helps determine when we display the new message indicator */
    messageCount?: number;
    /** Optional text to display when new messages arrive */
    newMessageText?: string;
    /** Children content to display inside the body. */
    children?: React.ReactNode;
    /** Optional loading state (defaults to false)*/
    loading?: boolean;
    /** Function to load previous messages (optional) */
    loadPreviousMessages?: () => void;
    /** subscribe to when a new message is received (optional) */
    onNewMessage?: (handler: () => void) => void;
};

/**
 * Body component that displays an optional title/subtitle.
 *
 * @param {BodyProps} props - The properties to configure the Body component.
 * @returns {JSX.Element} The rendered Body component.
 */
export const Body: React.FC<BodyProps> = ({
    title,
    subtitle,
    latestMessageText,
    loadMessagesText,
    newMessageText,
    children,
    loading = false,
    loadPreviousMessages,
    onNewMessage,
}) => {
    // Scroll Manager
    const bodyRef = useRef<HTMLDivElement>(null);

    const [isNearTop, setIsNearTop] = useState(false);
    const [newMessageIndicatorVisible, setNewMessageIndicatorVisible] = useState(false);
    const [scrollDownOpacity, setScrollDownOpacity] = useState(0);

    const autoScroller = useRef(new AutoScroller(setScrollDownOpacity, setNewMessageIndicatorVisible)).current;

    useEffect(() => {
        if (bodyRef.current && !loading) {
            const callback = () => {
                autoScroller.onDomChanged(bodyRef.current as HTMLDivElement);
            };

            const observer = new MutationObserver(callback);
            observer.observe(bodyRef.current, {
                attributes: true,
                childList: true,
                subtree: true,
                characterData: true,
            });

            return () => observer.disconnect();
        }
    }, [bodyRef, loading]);

    useEffect(() => {
        if (onNewMessage) {
            const unsubscribe = onNewMessage(() => {
                autoScroller.newMessageReceived(bodyRef.current);
            });
            return unsubscribe;
        }
    }, [onNewMessage, autoScroller]);


    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (bodyRef.current) {
            autoScroller.onScroll(bodyRef.current);
        }

        // Check if the user is near the top
        const target = event.currentTarget as HTMLDivElement;
        const isScrolledNearTop = target.scrollTop <= 75;
        setIsNearTop(isScrolledNearTop);
    };

    const scrollToBottom = () => {
        if (bodyRef.current) {
            autoScroller.scrollToBottom(bodyRef.current);
        }
    };

    if (loading) {
        return (
            <Styled.Body $loading={true}>
                <Styled.LoadingWrapper>
                    <LoadingIndicator height='50px' width='50px' />
                </Styled.LoadingWrapper>
            </Styled.Body>
        )
    }

    return (
        <Styled.Body ref={bodyRef} onScroll={handleScroll}>
            {loadPreviousMessages && isNearTop && (
                <Styled.LoadMessagesIndicator
                    onClick={loadPreviousMessages}
                    style={{ opacity: 1 }}
                    aria-label={loadMessagesText}
                    tabIndex={0}
                    role='button'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); // Prevent default scrolling for Space key
                            loadPreviousMessages();
                        }
                    }}
                >
                    <Styled.MoreIcon><ChevronDown /></Styled.MoreIcon> {loadMessagesText}
                </Styled.LoadMessagesIndicator>
            )}
            {!loadPreviousMessages &&
                <Styled.TitleWrapper>
                    {title && <Styled.Title>{title}</Styled.Title>}
                    {subtitle && <Styled.Subtitle>{subtitle}</Styled.Subtitle>}
                </Styled.TitleWrapper>
            }
            {children}
            {newMessageIndicatorVisible ? (
                <Styled.NewMessageIndicator
                    onClick={scrollToBottom}
                    style={{ opacity: scrollDownOpacity }}
                    aria-label={newMessageText}
                    tabIndex={0}
                    role='button'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); // Prevent default scrolling for Space key
                            scrollToBottom();
                        }
                    }}
                >
                    <Styled.NewIcon><NewMessageIcon /></Styled.NewIcon> {newMessageText}
                </Styled.NewMessageIndicator>
            ) : scrollDownOpacity > 0 && (
                <Styled.LatestMessageIndicator
                    onClick={scrollToBottom}
                    style={{ opacity: scrollDownOpacity }}
                    aria-label={latestMessageText}
                    tabIndex={0}
                    role='button'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); // Prevent default scrolling for Space key
                            scrollToBottom();
                        }
                    }}
                >
                    <Styled.LatestIcon><ChevronDown /></Styled.LatestIcon> {latestMessageText}
                </Styled.LatestMessageIndicator>
            )}
        </Styled.Body>
    );
};
